<template>
  <div class="rounded-2 bg-base" :style="{ fontFamily: data?.theme?.font_family }">
    <div
      class="w-full overflow-auto"
      :class="{
        'max-h-[calc(100vh-350px)]': !isPrintDisplayMode && !isScribbleChild,
        '!h-auto !overflow-visible': isPrintDisplayMode,
        'overflow-auto': isScribbleChild,
      }"
      :style="style"
    >
      <!-- Use different table structures based on breakGroupsOnNewPage setting -->
      <template v-if="shouldBreakGroupsOnNewPage && isPrintDisplayMode">
        <!-- Multiple tables with page breaks between groups -->
        <div
          v-for="(group, groupIndex) in sortedGroupedData"
          :key="group.value"
          class="group-container"
          :class="{ 'print:break-before-page': groupIndex > 0 }"
        >
          <table class="w-full border-collapse table-fixed" :style="{ '--column-count': visibleColumns.length }">
            <colgroup>
              <col v-for="(column, colIndex) in visibleColumns" :key="`col-${colIndex}`" class="data-col" />
            </colgroup>

            <!-- Table header for each group when breaking on new page -->
            <TableHeader
              break-groups-on-new-page
              :columns="visibleColumns"
              :current-group-header="group.value"
              :current-sorting="currentSorting"
              :group-column-header="groupDisplayName"
              :group-header-background-color="groupHeaderBackgroundColor"
              :group-header-foreground-color="groupHeaderForegroundColor"
              :header-background-color="headerBackgroundColor"
              :header-foreground-color="headerForegroundColor"
              use-fixed-widths
              @sort="handleSort"
            />

            <tbody>
              <!-- Group data rows -->
              <tr
                v-for="(row, idx) in group.rows"
                :key="row.key"
                :style="{
                  borderBottom: '1px solid var(--primary6)',
                  backgroundColor: getRowBackgroundColor(idx, props.data, themeVars),
                }"
              >
                <td
                  v-for="(column, cellIndex) in visibleColumns"
                  :key="`cell-${cellIndex}`"
                  class="px-4 py-2 whitespace-normal align-middle overflow-hidden text-ellipsis"
                >
                  {{ formatValue(row[column.id], column.id) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <template v-else>
        <!-- Single table with no page breaks between groups -->
        <table
          ref="tableContainer"
          class="w-full border-collapse table-fixed"
          :style="{ '--column-count': visibleColumns.length }"
        >
          <colgroup>
            <col
              v-for="(column, colIndex) in visibleColumns"
              :key="`col-${colIndex}`"
              class="w-[calc(100%/var(--column-count))]"
            />
          </colgroup>

          <!-- Table header shown only once at the top, no group header here -->
          <TableHeader
            v-if="isPrintDisplayMode || sortedGroupedData.length > 0"
            :columns="visibleColumns"
            :current-sorting="currentSorting"
            :header-background-color="headerBackgroundColor"
            :header-foreground-color="headerForegroundColor"
            use-fixed-widths
            @sort="handleSort"
          />

          <!-- All groups in a single tbody -->
          <tbody>
            <template v-for="group in sortedGroupedData" :key="group.value">
              <!-- Group header row -->
              <tr>
                <th
                  class="font-bold text-left py-2 px-4"
                  :colspan="visibleColumns.length"
                  :style="{
                    backgroundColor: groupHeaderBackgroundColor,
                    color: groupHeaderForegroundColor,
                  }"
                >
                  {{ groupDisplayName }}: {{ group.value }}
                </th>
              </tr>

              <!-- Group data rows -->
              <tr
                v-for="(row, idx) in group.rows"
                :key="row.key"
                :style="{
                  borderBottom: '1px solid var(--primary6)',
                  backgroundColor: getRowBackgroundColor(idx, props.data, themeVars),
                }"
              >
                <td
                  v-for="(column, cellIndex) in visibleColumns"
                  :key="`cell-${cellIndex}`"
                  class="px-4 py-2 whitespace-normal align-middle overflow-hidden text-ellipsis"
                >
                  {{ formatValue(row[column.id], column.id) }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </template>
    </div>

    <!-- Add a page break after the last group in print mode -->
    <div
      v-if="isPrintDisplayMode && shouldBreakGroupsOnNewPage && sortedGroupedData.length > 0"
      class="print:break-after-page"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { type ColumnSort } from '@tanstack/table-core'

import TableHeader from './DynamicDataTable.raw.header.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useThemeVars from '@lib/composables/useThemeVars'
import useDataTablePrint from '@canvas-builder/composables/useDataTablePrint'

import { getContrastTextColor } from '@lib/util/color'
import { ComponentTypes, type ComponentNode, type DynamicDataTableProps } from '@canvas-builder/types/canvas'
import { getRowBackgroundColor, formatCellValue } from './DynamicDataTable.utils'
import type { CanvasMetadataRetrieve } from '@lib/types/canvases'

interface DynamicDataTableUserSorting {
  field: string
  direction: 'asc' | 'desc'
}

interface DynamicDataTableCanvasMetadata {
  user_sorting?: DynamicDataTableUserSorting
}

const props = defineProps<{
  data: DynamicDataTableProps
  tableData: any[]
  headerBackgroundColor: string
  groupHeaderBackgroundColor: string
  canvasMetadata?: CanvasMetadataRetrieve['config']
  componentId: string
  breakGroupsOnNewPage?: boolean
  parentType?: ComponentNode['type']
  style?: Record<string, any>
}>()

const emit = defineEmits<{
  sortChanged: [s: ColumnSort | null]
}>()

const { isPrintDisplayMode } = useCanvas()
const themeVars = useThemeVars()
const { applyRowPrintStyles } = useDataTablePrint()

const tableContainer = ref<HTMLElement | null>(null)

const isScribbleChild = computed(() => {
  return props.parentType === ComponentTypes.Scribble
})

const shouldBreakGroupsOnNewPage = computed(() => {
  return props.breakGroupsOnNewPage !== false
})

interface Column {
  id: string
  header: string
  enableSorting: boolean
  sortDescFirst?: boolean
}

interface GroupData {
  value: any
  rows: any[]
}

// Initialize sorting based on canvas metadata or initial sorting from props
const currentSorting = ref<ColumnSort | null>(null)
const hasDefaultGroupSorting = ref<boolean>(false)

onMounted(() => {
  // First check for user sorting in canvas metadata
  const componentCanvasMetadata: DynamicDataTableCanvasMetadata =
    props.canvasMetadata?.presentation_component_metadata?.[props.componentId]

  const userSorting = componentCanvasMetadata?.user_sorting
  if (userSorting) {
    // Check if the field exists in data.properties and is sortable
    const sortableField = props.data.properties?.find((prop) => prop.key === userSorting.field && prop.sort?.enabled)
    if (sortableField) {
      currentSorting.value = {
        id: userSorting.field,
        desc: userSorting.direction === 'desc',
      }
      return
    }
  }

  // If no user sorting, check for default sorting in properties
  const defaultSortProp = props.data.properties?.find((prop) => prop.sort?.default && prop.sort?.enabled)
  if (defaultSortProp) {
    currentSorting.value = {
      id: defaultSortProp.key,
      desc: defaultSortProp.sort?.direction === 'desc',
    }
    hasDefaultGroupSorting.value = defaultSortProp.group === true
  }

  if (isPrintDisplayMode.value && tableContainer.value) {
    applyRowPrintStyles(tableContainer.value)
  }
})

const groupHeaderForegroundColor = computed(() => {
  return getContrastTextColor(props.groupHeaderBackgroundColor)
})

const headerForegroundColor = computed(() => {
  return getContrastTextColor(props.headerBackgroundColor)
})

const columns = computed<Column[]>(() => {
  if (!props.data?.properties) return []

  const groupProperty = props.data.properties.find((prop) => prop.group)
  const visibleProperties = props.data.properties.filter((prop) => {
    if (prop.group) return false
    if (isPrintDisplayMode.value && prop.printable === false) return false
    return true
  })

  return [
    // Group column
    {
      id: groupProperty?.key || '',
      header: groupProperty?.display_name || groupProperty?.key || 'Group',
      enableSorting: false,
    },
    // Other columns
    ...visibleProperties.map((prop) => ({
      id: prop.key,
      header: prop.display_name || prop.key,
      enableSorting: prop.sort?.enabled ?? false,
      sortDescFirst: prop.sort?.direction === 'desc',
    })),
  ]
})

const visibleColumns = computed(() => {
  return columns.value.filter((_, index) => index !== 0)
})

const groupProperty = computed(() => {
  return props.data?.properties?.find((prop) => prop.group)
})

const groupKey = computed(() => groupProperty.value?.key || '')
const groupDisplayName = computed(() => groupProperty.value?.display_name || groupProperty.value?.key || 'Group')

const groupedData = computed<GroupData[]>(() => {
  if (!groupProperty.value || !props.tableData.length) return []

  // Group the data
  const groups = new Map<any, any[]>()
  const gKey = groupKey.value

  props.tableData.forEach((row) => {
    const groupValue = row[gKey]
    if (!groups.has(groupValue)) {
      groups.set(groupValue, [])
    }
    groups.get(groupValue)?.push({
      ...row,
      key: `row-${row[gKey]}-${row.key}`,
    })
  })

  // Sort groups by their values (false before true, etc.)
  const sortedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
    if (typeof a === 'boolean' && typeof b === 'boolean') return a ? 1 : -1
    return String(a).localeCompare(String(b))
  })

  return sortedGroups.map(([value, rows]) => ({
    value,
    rows,
  }))
})

const sortedGroupedData = computed(() => {
  if (!currentSorting.value) return groupedData.value

  if (currentSorting.value.id === groupKey.value) {
    return [...groupedData.value].sort((groupA, groupB) => {
      return compareValues(groupA.value, groupB.value, currentSorting.value!.desc)
    })
  }

  let baseGroupedData = groupedData.value

  if (hasDefaultGroupSorting.value && currentSorting.value.id !== groupKey.value) {
    const groupProperty = props.data?.properties?.find((prop) => prop.group && prop.sort?.default)
    if (groupProperty) {
      baseGroupedData = [...groupedData.value].sort((groupA, groupB) => {
        return compareValues(groupA.value, groupB.value, groupProperty.sort?.direction === 'desc')
      })
    }
  }

  return baseGroupedData.map((group) => ({
    ...group,
    rows: [...group.rows].sort((a, b) => {
      return compareValues(a[currentSorting.value!.id], b[currentSorting.value!.id], currentSorting.value!.desc)
    }),
  }))
})

function handleSort(columnId: string) {
  if (currentSorting.value?.id === columnId) {
    if (currentSorting.value.desc) {
      currentSorting.value = null
    } else {
      currentSorting.value = { id: columnId, desc: true }
    }
  } else {
    currentSorting.value = { id: columnId, desc: false }
  }

  emit('sortChanged', currentSorting.value)
}

function compareValues(valueA: any, valueB: any, isDescending: boolean = false): number {
  const compareResult =
    typeof valueA === 'string' || typeof valueB === 'string'
      ? String(valueA).localeCompare(String(valueB))
      : Number(valueA) - Number(valueB)

  return isDescending ? -compareResult : compareResult
}

function formatValue(value: any, columnId: string): string {
  if (!props.data?.properties) {
    return value !== undefined ? String(value) : ''
  }

  const property = props.data.properties.find((prop) => prop.key === columnId)
  if (!property) {
    return value !== undefined ? String(value) : ''
  }

  return formatCellValue(value, property)
}
</script>
